import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import GoBackButton from '../../components/FormComponents/GoBackButton';
import { PageTitle } from '../../utils/PageTitle';
import { useForm } from "react-hook-form";
import { useFirestore } from '../../context/FirestoreContext';
import { useSessionStorage } from '../../logic/useSessionStorage';

const EditHarvestYear = () => {
    PageTitle("Edit Harvestyear");
    const [currentWinery, setCurrentWinery] = useSessionStorage('currentwinery', '');
    const { register, handleSubmit } = useForm();
    const params = useParams();
    const navigate = useNavigate();
    const { getHarvestYears } = useFirestore();
    const { updateHarvestYear } = useFirestore();
    const [harvestYears, setHarvestYears] = useState(undefined);
    const [harvestYear, setHarvestYear] = useState(undefined);
    const [errorMessage, setErrorMessage] = useState(null);
    const [isLoading, setLoading] = useState(true);
    const { t } = useTranslation();

    useEffect(() => {
        getHarvestYearsFromDB();
    }, [])

    const getHarvestYearsFromDB = async () => {
        const harvestYears = await getHarvestYears(currentWinery);

        harvestYears.forEach(year => {
            year.startDate = year.startDate.toDate();
            year.endDate = year.endDate.toDate();
        })

        setHarvestYears(harvestYears);

        //searching current harvestyear
        let harvestYear = harvestYears.find(harvestYear => harvestYear.id === params.id);
        if (harvestYear === undefined) {
            navigate("/harvestyears");
            return;
        }

        setHarvestYear(harvestYear);
        setLoading(false);
    }

    const onSubmit = async (data) => {
        setLoading(true);

        let startDateString = data.startDate ? data.startDate.split("-") : harvestYear.startDate.toISOString().split("T")[0].split("-");
        const startDate = new Date(startDateString[0], startDateString[1] - 1, startDateString[2]);
      
        let endDateString = data.endDate ? data.endDate.split("-") : harvestYear.endDate.toISOString().split("T")[0].split("-");
        const endDate = new Date(endDateString[0], endDateString[1] - 1, endDateString[2]);

        const updatedHarvestYear = {
            name: data.name,
            startDate: startDate,
            endDate: endDate,
        }

        if (!data.name && harvestYear.name) {
            updatedHarvestYear.name = harvestYear.name;
          }


        const validationResult = await validateInput(updatedHarvestYear);

        if (validationResult) {
            updateHarvestYear(currentWinery, harvestYear.id, updatedHarvestYear);
            navigate("/harvestyears");
        }

        setLoading(false);
    }

    const validateInput = async ({ name, startDate, endDate }) => {
        //checking if any data has been modified
        if (name === harvestYear.name &&
            startDate.valueOf() === harvestYear.startDate.valueOf() &&
            endDate.valueOf() === harvestYear.endDate.valueOf()) {
            setErrorMessage(t('translation.harvestYears.errorMessages.nothingChanged'));
            return false;
        } else {
            setErrorMessage(null);
        }

        //checking if date range start and end is set correctly
        if (startDate.valueOf() >= endDate.valueOf()) {
            setErrorMessage(t('translation.harvestYears.errorMessages.endBeforeStartDate') + ` ${startDate.getDate()}-${startDate.getMonth() + 1}-${startDate.getFullYear()}`);
            return false;
        } else {
            setErrorMessage(null);
        }

        //checking if date range overlaps with any existing HarvestYear
        const state = harvestYears.every(harvestYear => {
            //skip checking if harvestyear is the one being edited right now
            if (harvestYear.id === params.id) return true;

            if (startDate.getTime() >= harvestYear.startDate.getTime() && startDate.getTime() <= harvestYear.endDate.getTime() ||
                harvestYear.startDate.getTime() >= startDate.getTime() && harvestYear.startDate.getTime() <= endDate.getTime()) {
                setErrorMessage(t('translation.harvestYears.errorMessages.datesOverlapping') + ": " + harvestYear.name);
                return false;
            } else {
                setErrorMessage(null);
                return true;
            }
        });
        if (!state) return false;
        return true;
    }

    const getDateString = date => {
        const day = date.getDate() < 10 ? "0" + date.getDate() : date.getDate();
        const month = date.getMonth() + 1 < 10 ? "0" + (date.getMonth() + 1) : date.getMonth() + 1;
        const year = date.getFullYear();

        return `${year}-${month}-${day}`
    }

    const formClassname = `ui big form twelve wide column ${isLoading ? 'loading' : ''} `;
    return <>
        <h1 style={{ color: "#7f7f7f" }}> {t('translation.harvestYears.harvestYearInfo')} </h1>
        <div className='container'>
            <div className="ui grid stackable" style={{ justifyContent: 'center' }}>
                <GoBackButton />

                <form
                    className={formClassname}
                    style={{
                        border: '3px solid #B3B3B3',
                        marginTop: '50px',
                        borderRadius: '50px',
                        borderColor: '#ac9e66',
                    }}
                    onSubmit={handleSubmit(onSubmit)}>

                    <div className="fields" style={{ justifyContent: 'center' }}>
                        <div className="twelve wide field">
                            <label>
                                {t("translation.words.name")}
                                {harvestYear === undefined
                                    ? <input type="text" {...register('name')} htmlFor="name" required="required" />
                                    :
                                    <input
                                        type="text"
                                        {...register('name')}
                                        htmlFor="name"
                                        required="required"
                                        defaultValue={harvestYear.name} />
                                }
                            </label>
                        </div>
                    </div>

                    <div className="fields" style={{ justifyContent: 'center' }}>
                        <div className="twelve wide field">
                            <label>
                                {t("translation.harvestYears.startDate")}
                                {harvestYear === undefined
                                    ? <input
                                    type="date"
                                    {...register('startDate')}
                                    htmlFor="startDate"
                                    required="required" />
                                    : <input
                                    type="date"
                                    {...register('startDate')}
                                    htmlFor="startDate"
                                    required="required"
                                    defaultValue={getDateString(harvestYear.startDate)} />
                                }
                            </label>
                        </div>
                    </div>

                    <div className="fields" style={{ justifyContent: 'center' }}>
                        <div className="twelve wide field">
                            <label>
                                {t("translation.harvestYears.endDate")}
                                {harvestYear === undefined
                                    ? <input
                                    type="date"
                                    {...register('endDate')}
                                    htmlFor="endDate"
                                    required="required" />
                                    : <input
                                    type="date"
                                    {...register('endDate')}
                                    htmlFor="endDate"
                                    required="required"
                                    defaultValue={getDateString(harvestYear.endDate)} />
                                }
                            </label>
                        </div>
                    </div>

                    <div style={{ textAlign: "center" }}>
                        <button type="submit" className="ui submit large grey button right floated bttn-profile"
                            style={{ float: 'none', marginTop: '30px' }}>
                            {t("translation.words.save")}
                        </button>
                        {errorMessage &&
                            <p style={{ color: 'red', fontWeight: 'bold', fontSize: '25px' }}>{errorMessage}</p>
                        }
                    </div>
                </form>
            </div>
        </div>
    </>;
}

export default EditHarvestYear